import React, { useEffect } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Alert,
  Button,
  Input,
  Tooltip,
  Popconfirm,
} from '@surecompanies/core_components'
import { Stack, TextColors, Typography } from '@sureco/design-system'
import RHFInput from '../../PlanBenefitOverrides/components/form/RHFInput'
import RHFSelect from '../../PlanBenefitOverrides/components/form/RHFSelect'
import { useDebounce } from 'hooks/useDebounce'
import OverrideAuditStrip from '../../../components/OverrideAuditStrip'
import {
  PlanLocationOverrideFormSchema,
  apiPayloadToForm,
  flatToApiPayload,
  type LocationViewMetadata,
  type PlanLocationOverrideFormValues,
} from './planLocationOverrideForm.schema'
import { US_STATE_OPTIONS } from '../../../constants'
import {
  useCheckDuplicateLocationOverrideRule,
  LOCATION_OVERRIDE_CHECK_DUPLICATE_QUERY_KEY,
} from '../hooks/useCheckDuplicateLocationOverrideRule'
import { useDeleteLocationOverrideRule } from '../hooks/useDeleteLocationOverrideRule'
import { useQueryClient } from '@tanstack/react-query'
import type { CreatePlanLocationOverrideRuleRequestDto } from '../../../api/dto/plan-location-override-rules.dto'
import { relativeTimeString } from 'enrollment-platform/utils/relative-time-string'
import styles from '../../../components/PlanOverridesComponents.module.scss'

const DUPLICATE_CHECK_DEBOUNCE_MS = 400
const CURRENT_YEAR = new Date().getFullYear()

interface DigitsOnlyRHFInputProps {
  name: keyof PlanLocationOverrideFormValues
  control: ReturnType<typeof useForm<PlanLocationOverrideFormValues>>['control']
  label: string
  required?: boolean
  placeholder?: string
  maxLength?: number
  disabled?: boolean
}

function DigitsOnlyRHFInput({
  name,
  control,
  label,
  required,
  placeholder,
  maxLength,
  disabled,
}: DigitsOnlyRHFInputProps) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Stack space={2}>
          <Typography>
            {label}
            {required && <span className="requiredMark">*</span>}
          </Typography>
          <Input
            {...field}
            value={field.value ?? ''}
            onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
            placeholder={placeholder}
            maxLength={maxLength}
            inputMode="numeric"
            disabled={disabled}
            status={fieldState.error ? 'error' : undefined}
          />
          {fieldState.error && (
            <Typography color={TextColors.error}>
              {fieldState.error.message}
            </Typography>
          )}
        </Stack>
      )}
    />
  )
}

export type LocationFormMode = 'create' | 'edit' | 'view'

export interface LocationServerFieldError {
  field: string
  errors: string[]
}

interface Props {
  mode?: LocationFormMode
  defaultValues?: Partial<PlanLocationOverrideFormValues>
  serverErrors?: LocationServerFieldError[]
  metadata?: LocationViewMetadata
  onSubmit?: (data: CreatePlanLocationOverrideRuleRequestDto) => Promise<void>
  onCancel?: () => void
  isSubmitting?: boolean
  onDuplicateFound?: (ruleId: string) => void
  extraContent?: React.ReactNode
  step?: number
}

export default function PlanLocationOverrideForm({
  mode = 'create',
  defaultValues,
  serverErrors,
  metadata,
  onSubmit,
  onCancel,
  isSubmitting = false,
  onDuplicateFound,
  extraContent,
  step,
}: Props) {
  const isEditMode = mode === 'edit'
  const isReadOnly = mode === 'view'
  const isCreateMode = mode === 'create'

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, submitCount },
  } = useForm<PlanLocationOverrideFormValues>({
    resolver: zodResolver(PlanLocationOverrideFormSchema),
    defaultValues: {
      year: CURRENT_YEAR,
      state: '',
      zipCode: '',
      countyFipsCode: '',
      county: '',
      ratingArea: '',
      sourceUrl: '',
      notes: '',
      ...defaultValues,
    },
  })

  useEffect(() => {
    if (!serverErrors || serverErrors.length === 0) return
    serverErrors.forEach(({ field, errors: fieldErrors }) => {
      if (!field || fieldErrors.length === 0) return
      const mappedField = field.startsWith('match.')
        ? (field.slice('match.'.length) as keyof PlanLocationOverrideFormValues)
        : field.startsWith('params.')
          ? (field.slice(
              'params.'.length
            ) as keyof PlanLocationOverrideFormValues)
          : (field as keyof PlanLocationOverrideFormValues)
      setError(mappedField, { type: 'server', message: fieldErrors.join(', ') })
    })
  }, [serverErrors, setError])

  const year = useWatch({ control, name: 'year' })
  const state = useWatch({ control, name: 'state' })
  const zipCode = useWatch({ control, name: 'zipCode' })
  const countyFipsCode = useWatch({ control, name: 'countyFipsCode' })
  const county = useWatch({ control, name: 'county' })
  const ratingArea = useWatch({ control, name: 'ratingArea' })

  const duplicateKey =
    isCreateMode &&
    year &&
    state &&
    zipCode &&
    /^\d{5}$/.test(zipCode) &&
    countyFipsCode &&
    /^\d{5}$/.test(countyFipsCode) &&
    ratingArea
      ? `${year}|${state}|${zipCode}|${countyFipsCode}|${ratingArea}`
      : null
  const debouncedDuplicateKey = useDebounce(
    duplicateKey,
    DUPLICATE_CHECK_DEBOUNCE_MS
  )
  const duplicateQuery = debouncedDuplicateKey
    ? { year: Number(year), state, zipCode, countyFipsCode, ratingArea }
    : {}
  const { data: duplicateData } = useCheckDuplicateLocationOverrideRule(
    duplicateQuery,
    Boolean(debouncedDuplicateKey)
  )
  const showDuplicateAlert = duplicateData?.exists === true
  const duplicateRule = duplicateData?.exists ? duplicateData.rule : null

  const queryClient = useQueryClient()
  const { mutate: deleteDuplicate, isPending: isDeletingDuplicate } =
    useDeleteLocationOverrideRule()

  const handleDeleteDuplicate = () => {
    if (!duplicateRule) return
    deleteDuplicate(duplicateRule.id, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [LOCATION_OVERRIDE_CHECK_DUPLICATE_QUERY_KEY],
        })
      },
    })
  }

  const handleFormSubmit = async (values: PlanLocationOverrideFormValues) => {
    if (!onSubmit) return
    const payload = flatToApiPayload(values)
    await onSubmit(payload)
  }

  const formHasErrors = submitCount > 0 && Object.keys(errors).length > 0

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      {isCreateMode && showDuplicateAlert && duplicateRule && (
        <div className={styles.duplicateBanner}>
          <div className={styles.duplicateBannerIcon}>⚠</div>
          <div className={styles.duplicateBannerBody}>
            <span className={styles.duplicateBannerTitle}>
              Rule already exists for this combination
            </span>
            <span className={styles.duplicateBannerValue}>
              {duplicateRule.match.state}
              <span className={styles.duplicateBannerDot}> · </span>
              {duplicateRule.match.zipCode}
              <span className={styles.duplicateBannerDot}> · </span>
              FIPS {duplicateRule.match.countyFipsCode}
              <span className={styles.duplicateBannerDot}> · </span>
              {duplicateRule.match.year}
            </span>
            <span className={styles.duplicateBannerMeta}>
              <span
                className={`${styles.statusTag} ${duplicateRule.status === 'APPROVED' ? styles.statusActive : duplicateRule.status === 'REJECTED' ? styles.statusInactive : styles.statusDraft}`}
              >
                {duplicateRule.status}
              </span>
              <span className={styles.duplicateBannerDot}>·</span>
              {relativeTimeString(duplicateRule.updatedAt)}
            </span>
          </div>
          <div className={styles.duplicateBannerActions}>
            <Button
              type="primary"
              size="small"
              onClick={() => onDuplicateFound?.(duplicateRule.id)}
              disabled={!onDuplicateFound}
            >
              Edit
            </Button>
            <Popconfirm
              title="Delete this rule?"
              description="This will permanently delete the existing rule."
              okText="Delete"
              okType="danger"
              cancelText="Cancel"
              onConfirm={handleDeleteDuplicate}
            >
              <Button
                type="default"
                size="small"
                danger
                loading={isDeletingDuplicate}
              >
                Delete
              </Button>
            </Popconfirm>
          </div>
        </div>
      )}

      {formHasErrors && (
        <Alert
          type="error"
          showIcon
          message="Please fix the errors below before saving."
          style={{ marginBottom: 16 }}
        />
      )}

      <div className={styles.stepList}>
        {/* Step 1 — Identity (immutable after create) */}
        {(step === undefined || step === 1) && (
          <div className={styles.step}>
            <div className={styles.stepLeft}>
              <div className={styles.stepBadge}>1</div>
              <div className={styles.stepRail} />
            </div>
            <div className={styles.stepContent}>
              <p className={styles.stepTitle}>Target</p>
              <div className={styles.stepFieldRow}>
                <RHFInput
                  name="year"
                  control={control}
                  label="Year"
                  required
                  type="number"
                  placeholder="e.g. 2025"
                  disabled={isReadOnly || isEditMode}
                />
                <RHFSelect
                  name="state"
                  control={control}
                  label="State"
                  required
                  options={US_STATE_OPTIONS}
                  placeholder="Select state"
                  showSearch
                  optionFilterProp="label"
                  disabled={isReadOnly || isEditMode}
                />
                <DigitsOnlyRHFInput
                  name="zipCode"
                  control={control}
                  label="Zip Code"
                  required
                  placeholder="5 digits"
                  maxLength={5}
                  disabled={isReadOnly || isEditMode}
                />
                <DigitsOnlyRHFInput
                  name="countyFipsCode"
                  control={control}
                  label="County FIPS Code"
                  required
                  placeholder="5-digit FIPS"
                  maxLength={5}
                  disabled={isReadOnly || isEditMode}
                />
                <DigitsOnlyRHFInput
                  name="ratingArea"
                  control={control}
                  label="Rating Area"
                  required
                  placeholder="Rating area"
                  disabled={isReadOnly || isEditMode}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2 — Override type (always editable) */}
        {(step === undefined || step === 2) && (
          <div className={styles.step}>
            <div className={styles.stepLeft}>
              <div className={styles.stepBadge}>2</div>
            </div>
            <div className={`${styles.stepContent} ${styles.stepContentLast}`}>
              <p className={styles.stepTitle}>Override type</p>
              <div className={styles.stepFieldRow}>
                <RHFInput
                  name="county"
                  control={control}
                  label="County"
                  required
                  placeholder="County name"
                  disabled={isReadOnly}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3 — Evidence documents (rendered via extraContent) */}
        {(step === undefined || step === 3) && extraContent}
      </div>

      {!isReadOnly && (
        <div className={styles.formFooter}>
          {isEditMode && metadata ? (
            <OverrideAuditStrip metadata={metadata} />
          ) : (
            <div />
          )}
          <div className={styles.formFooterActions}>
            <Button type="default" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Tooltip
              title={
                showDuplicateAlert
                  ? 'Resolve the duplicate rule above before saving.'
                  : !county
                    ? 'Enter a county name before saving.'
                    : !isEditMode && !year
                      ? 'Enter a year before saving.'
                      : !isEditMode && !state
                        ? 'Select a state before saving.'
                        : !isEditMode && (!zipCode || !/^\d{5}$/.test(zipCode))
                          ? 'Enter a valid 5-digit zip code before saving.'
                          : !isEditMode &&
                              (!countyFipsCode ||
                                !/^\d{5}$/.test(countyFipsCode))
                            ? 'Enter a valid 5-digit county FIPS code before saving.'
                            : !isEditMode && !ratingArea
                              ? 'Enter a rating area before saving.'
                              : undefined
              }
            >
              <Button
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                disabled={
                  isSubmitting ||
                  showDuplicateAlert ||
                  !county ||
                  (!isEditMode &&
                    (!year ||
                      !state ||
                      !zipCode ||
                      !/^\d{5}$/.test(zipCode) ||
                      !countyFipsCode ||
                      !/^\d{5}$/.test(countyFipsCode) ||
                      !ratingArea))
                }
              >
                Save
              </Button>
            </Tooltip>
          </div>
        </div>
      )}
    </form>
  )
}

// Export helpers for use in parent components (view/edit modal)
export { apiPayloadToForm }
